/* eslint-disable no-console */
// Shared payment cache utilities for ws-track-my-offer

/**
 * Extract vehicle UUID from URL path
 * @param {string} pathname - window.location.pathname
 * @returns {string|null} - 32-character UUID or null if not found
 */
export const extractVehicleUUID = (pathname) => {
	const pathMatch = pathname.match(/([a-f0-9]{32})\.htm$/);
	return pathMatch ? pathMatch[1] : null;
};

/**
 * Get cached payment data from localStorage
 * @param {string} uuid - Vehicle UUID
 * @returns {Object|null} - Parsed payment data or null if not found/invalid
 */
export const getCachedPaymentData = (uuid) => {
	if (!uuid) return null;

	try {
		const cacheKey = `payment_cache_${uuid}`;
		const cachedData = localStorage.getItem(cacheKey);
		if (!cachedData) return null;

		const parsedData = JSON.parse(cachedData);
		// Support both old format (parsedData) and new format (parsedData.data)
		return parsedData.data || parsedData;
	} catch (e) {
		console.error('Error parsing cached payment data:', e);
		return null;
	}
};

/**
 * Get wallet configuration from localStorage
 * @returns {Object|null} - Parsed wallet config or null if not found/invalid
 */
export const getWalletConfig = () => {
	try {
		const walletConfigStr = localStorage.getItem('CAIWalletConfig');
		return walletConfigStr ? JSON.parse(walletConfigStr) : null;
	} catch (e) {
		console.error('Error reading wallet config:', e);
		return null;
	}
};

/**
 * Check if user has interacted with wallet
 * @param {Object} walletConfig - Wallet configuration object
 * @returns {boolean} - True if user has interacted
 */
export const hasWalletInteractions = (walletConfig) => {
	if (!walletConfig) return false;

	return !!(
		walletConfig.interactions?.hasOpenedWallet ||
		walletConfig.interactions?.hasUpdatedWallet ||
		walletConfig.interactions?.hasUpdatedNDSCalc ||
		walletConfig.interactions?.paymentEmphasis
	);
};

/**
 * Get appropriate loan term based on user interactions
 * @param {Object} walletConfig - Wallet configuration object
 * @returns {string} - Loan term as string (e.g., "60")
 */
export const getLoanTerm = (walletConfig) => {
	const hasInteracted = hasWalletInteractions(walletConfig);

	if (hasInteracted && walletConfig?.loanTerm) {
		return walletConfig.loanTerm.toString();
	}

	// Default to Esntial's 60-month term for users who haven't interacted
	return '60';
};

/**
 * Detect available payment types from payment data
 * @param {Object} ratesData - Payment rates data
 * @returns {Object} - Object with hasFinance, hasLease, hasCash flags
 */
export const detectPaymentTypes = (ratesData) => {
	if (!ratesData)
		return { hasFinance: false, hasLease: false, hasCash: false };

	const hasFinance = Object.keys(ratesData.paymentOffers || {}).some(
		(term) => ratesData.paymentOffers[term]?.[0]?.payment > 0
	);

	const hasLease = Object.keys(ratesData.leaseOffers || {}).some(
		(term) => ratesData.leaseOffers[term]?.[0]?.payment > 0
	);

	const hasCash = ratesData.cashOffers?.['-1']?.[0]?.cashAmount > 0;

	return { hasFinance, hasLease, hasCash };
};

/**
 * Format currency amount for display
 * @param {number} amount - Raw amount
 * @param {string} defaultValue - Default value if amount is invalid
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount, defaultValue = '$---') => {
	if (!amount) return defaultValue;
	return `$${Math.floor(amount).toLocaleString()}`;
};

/**
 * Get payment types from any cached vehicle in localStorage
 * @returns {Object|null} - Payment types object or null if none found
 */
export const getPaymentTypesFromAnyCache = () => {
	try {
		const keys = Object.keys(localStorage);
		const paymentCacheKeys = keys.filter((key) =>
			key.startsWith('payment_cache_')
		);

		if (paymentCacheKeys.length === 0) {
			return null;
		}

		let foundTypes = null;

		paymentCacheKeys.some((cacheKey) => {
			try {
				const cachedData = localStorage.getItem(cacheKey);
				if (!cachedData) return false;

				const parsedData = JSON.parse(cachedData);
				const paymentData = parsedData.data || parsedData;

				if (!paymentData?.rates) return false;

				const creditRatingKey = Object.keys(paymentData.rates)[0];
				if (!creditRatingKey) return false;

				const ratesData = paymentData.rates[creditRatingKey];
				const types = detectPaymentTypes(ratesData);

				if (types.hasFinance || types.hasLease || types.hasCash) {
					foundTypes = types;
					return true; // Stop iterating
				}
				return false;
			} catch (e) {
				console.error(`Error parsing cache ${cacheKey}:`, e);
				return false;
			}
		});

		return foundTypes;
	} catch (e) {
		console.error('Error checking other caches:', e);
		return null;
	}
};
